'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { cn } from '@/lib/utils'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { FLAT_DOCS } from '@/constants/docs-nav'
import { VIEWS, VIEW_CATEGORIES } from '@/constants/views'

import {
    ArrowElbowDownLeftIcon,
    BrowsersIcon,
    FileTextIcon,
    MagnifyingGlassIcon,
    SettingsIcon
} from '@/components/icons/icon-set'

interface SearchEntry {
    title: string
    href: string
    group: string
    description: string
    kind: 'page' | 'view' | 'settings'
}

const ENTRIES: SearchEntry[] = [
    ...FLAT_DOCS.map(item => ({ title: item.title, href: item.href, group: item.group, description: item.description, kind: 'page' as const })),
    ...VIEWS.filter(view => !view.hidden).map(view => {
        const category = VIEW_CATEGORIES.find(item => item.group === view.group && item.slug === view.category)
        return {
            title: view.title,
            href: `/docs/${view.group}/${view.category}#${view.id}`,
            group: `${view.group === 'blocks' ? 'Blocks' : 'Templates'} · ${category?.title ?? ''}`,
            description: view.description,
            kind: 'view' as const
        }
    }),
    { title: 'Settings and your rules', href: '/settings', group: 'Settings', description: 'The project guide and your own rules for writing style, drawers, layout and more.', kind: 'settings' }
]

interface DocsSearchProps {
    open: boolean
    onOpenChange: (open: boolean) => void
}

/* Search that jumps to any docs page, block or template. Opens with Cmd K, Ctrl K or slash. */
export function DocsSearch({ open, onOpenChange }: DocsSearchProps) {
    const router = useRouter()
    const [query, setQuery] = useState('')
    const [active, setActive] = useState(0)
    const listRef = useRef<HTMLDivElement>(null)

    const results = useMemo(() => {
        const value = query.trim().toLowerCase()
        if (!value) return ENTRIES.filter(entry => entry.kind !== 'view').slice(0, 40)
        return ENTRIES
            .filter(entry => `${entry.title} ${entry.group} ${entry.description}`.toLowerCase().includes(value))
            .sort((a, b) => Number(!a.title.toLowerCase().startsWith(value)) - Number(!b.title.toLowerCase().startsWith(value)))
            .slice(0, 40)
    }, [query])

    useEffect(() => {
        setActive(0)
    }, [query])

    useEffect(() => {
        if (!open) setQuery('')
    }, [open])

    useEffect(() => {
        listRef.current?.querySelector(`[data-index='${active}']`)?.scrollIntoView({ block: 'nearest' })
    }, [active])

    const go = (entry?: SearchEntry) => {
        if (!entry) return
        onOpenChange(false)
        router.push(entry.href)
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className='top-[12vh] max-w-xl translate-y-0 gap-0 overflow-hidden p-0 sm:p-0'>
                <DialogTitle className='sr-only'>
                    Search the docs
                </DialogTitle>
                <div className='flex items-center gap-3 border-b border-border-subtle px-4'>
                    <MagnifyingGlassIcon className='size-5 shrink-0 text-icon-secondary' aria-hidden='true' />
                    <input
                        autoFocus
                        value={query}
                        onChange={event => setQuery(event.target.value)}
                        onKeyDown={event => {
                            if (event.key === 'ArrowDown') {
                                event.preventDefault()
                                setActive(index => Math.min(index + 1, results.length - 1))
                            }
                            if (event.key === 'ArrowUp') {
                                event.preventDefault()
                                setActive(index => Math.max(index - 1, 0))
                            }
                            if (event.key === 'Enter') {
                                event.preventDefault()
                                go(results[active])
                            }
                        }}
                        placeholder='Search components, blocks and templates'
                        aria-label='Search the docs'
                        aria-controls='docs-search-results'
                        aria-activedescendant={results[active] ? `docs-search-${active}` : undefined}
                        className='h-14 w-full bg-transparent text-base text-text-primary outline-none placeholder:text-text-placeholder'
                    />
                </div>
                <div ref={listRef} id='docs-search-results' role='listbox' aria-label='Results' className='max-h-[min(420px,60vh)] overflow-y-auto p-2'>
                    {results.length === 0 && (
                        <p className='px-3 py-8 text-center text-sm text-text-secondary'>
                            {`Nothing matches "${query}".`}
                        </p>
                    )}
                    {results.map((entry, index) => {
                        const Icon = entry.kind === 'view' ? BrowsersIcon : entry.kind === 'settings' ? SettingsIcon : FileTextIcon

                        return (
                            <button
                                key={`${entry.href}-${entry.title}`}
                                id={`docs-search-${index}`}
                                type='button'
                                role='option'
                                aria-selected={index === active}
                                data-index={index}
                                onMouseMove={() => setActive(index)}
                                onClick={() => go(entry)}
                                className={cn('flex w-full cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-left outline-none', index === active && 'bg-action-secondary-hover dark:bg-action-secondary-pressed')}
                            >
                                <Icon className='size-4 shrink-0 text-icon-secondary' aria-hidden='true' />
                                <span className='flex min-w-0 flex-1 flex-col'>
                                    <span className='truncate text-sm font-medium text-text-primary'>
                                        {entry.title}
                                    </span>
                                    <span className='truncate text-xs text-text-secondary'>
                                        {entry.group}
                                    </span>
                                </span>
                                {index === active && <ArrowElbowDownLeftIcon className='size-4 shrink-0 text-icon-tertiary' aria-hidden='true' />}
                            </button>
                        )
                    })}
                </div>
            </DialogContent>
        </Dialog>
    )
}
